import { Report } from '../swimlane/lib/types';
import type { SelectedCollectCollection } from './analysisContextLinks';

export const ANALYSIS_DRAFT_STORAGE_KEY = 'famla_company_analysis_draft';
const MAX_AGE_HOURS = 24;

export type AnalysisDraftPayload = {
    analysisId: string;
    reports: Report[];
    selectedMapIds: string[];
    selectedCollections?: SelectedCollectCollection[];
    lastUpdate: string;
    userId: string;
    companyId: string;
};

type StoredDraft = Partial<AnalysisDraftPayload> & {
    lastUpdate?: string;
};

function draftContextKey(userId: string, companyId: string): string {
    return `${String(userId).trim()}::${String(companyId).trim()}`;
}

function isDraftExpired(lastUpdate: string | undefined): boolean {
    if (!lastUpdate) return true;
    const updatedAt = new Date(lastUpdate);
    if (Number.isNaN(updatedAt.getTime())) return true;
    const hoursDiff = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60);
    return hoursDiff > MAX_AGE_HOURS;
}

/** Supprime le brouillon Insights (logout, changement compagnie, abandon explicite). */
export function clearAnalysisDraft(): void {
    try {
        localStorage.removeItem(ANALYSIS_DRAFT_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

export function saveAnalysisDraft(draft: AnalysisDraftPayload): void {
    try {
        if (!draft.reports.length || !draft.userId || !draft.companyId) {
            clearAnalysisDraft();
            return;
        }
        localStorage.setItem(
            ANALYSIS_DRAFT_STORAGE_KEY,
            JSON.stringify({
                ...draft,
                lastUpdate: new Date().toISOString(),
            }),
        );
    } catch (error) {
        console.error('Erreur lors de la sauvegarde du brouillon Insights:', error);
    }
}

export function loadAnalysisDraft(
    userId: string,
    companyId: string,
): AnalysisDraftPayload | null {
    if (!userId || !companyId) {
        clearAnalysisDraft();
        return null;
    }

    try {
        const stored = localStorage.getItem(ANALYSIS_DRAFT_STORAGE_KEY);
        if (!stored) return null;

        const session = JSON.parse(stored) as StoredDraft;
        const expected = draftContextKey(userId, companyId);
        const actual = draftContextKey(session.userId || '', session.companyId || '');

        if (!session.reports?.length || !session.analysisId || actual !== expected) {
            clearAnalysisDraft();
            return null;
        }

        if (isDraftExpired(session.lastUpdate)) {
            clearAnalysisDraft();
            return null;
        }

        return {
            analysisId: session.analysisId,
            reports: session.reports,
            selectedMapIds: session.selectedMapIds || [],
            selectedCollections: session.selectedCollections || [],
            lastUpdate: session.lastUpdate || new Date().toISOString(),
            userId,
            companyId,
        };
    } catch (error) {
        console.error('Erreur lors du chargement du brouillon Insights:', error);
        clearAnalysisDraft();
        return null;
    }
}

export function hasAnalysisDraft(userId: string, companyId: string): boolean {
    return loadAnalysisDraft(userId, companyId) !== null;
}
